import React, { useState, useEffect, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Routes, Route, Link, useLocation, Navigate } from "react-router-dom";

import "bootstrap/dist/css/bootstrap.min.css";
import "bootstrap-icons/font/bootstrap-icons.css";
import "./App.css";

import Login from "./components/Login";
import Register from "./components/Register";
import Home from "./components/Home";
import Profile from "./components/Profile";
import BoardUser from "./components/BoardUser";
import BoardModerator from "./components/BoardModerator";
import BoardAdmin from "./components/BoardAdmin";

import AddMember from "./components/family-members/AddMember";
import Member from "./components/family-members/Member";
import MemberList from "./components/family-members/MemberList";

import AddCostCenter from "./components/cost-centers/AddCostCenter";
import CostCenter from "./components/cost-centers/CostCenter";
import CostCenterList from "./components/cost-centers/CostCenterList";

import AddSupplier from "./components/suppliers/AddSupplier";
import Supplier from "./components/suppliers/Supplier";
import SupplierList from "./components/suppliers/SupplierList";

import AddPayment from "./components/payments/AddPayment";
import PaymentList from "./components/payments/PaymentList";
import Payment from "./components/payments/Payment";

import {
    clearMessage,
    selectIsLoggedIn,
    selectUser,
    logout,
} from "./features/auth/authSlice";

import authService from "./services/auth.service";

const ProtectedRoute = ({ component: Component, ...rest }) => {
    const [loading, setLoading] = useState(true);
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const dispatch = useDispatch();

    const verifyUserAuth = () => {
        // Make API call to verify if the user is authenticated
        authService
            .verify()
            .then((response) => {
                setIsAuthenticated(true);
            })
            .catch((error) => {
                // Handle unauthorized or other errors
                setIsAuthenticated(false);
                dispatch(logout()); // Dispatch logout action if not authenticated
            })
            .finally(() => {
                setLoading(false);
            });
    };

    useEffect(() => {
        // Run this effect only once on initial mount
        verifyUserAuth();
    }, []);

    useEffect(() => {
        // Add a new effect with isAuthenticated as a dependency
        // This will trigger the verification whenever isAuthenticated changes
        if (isAuthenticated) {
            verifyUserAuth();
        }
    }, [isAuthenticated]);

    // Render the component if the API call is successful and the user is authenticated
    if (loading) {
        return <div>Loading...</div>; // You can show a loading spinner here
    }

    return (
        <div>
            {isAuthenticated ? (
                <Component {...rest} />
            ) : (
                <Navigate to="/login" />
            )}
        </div>
    );
};

const App = () => {
    const [showModeratorBoard, setShowModeratorBoard] = useState(false);
    const [showAdminBoard, setShowAdminBoard] = useState(false);
    const [asideCollapsed, setAsideCollapsed] = useState(false);

    const toggleAside = () => {
        setAsideCollapsed(!asideCollapsed);
    };

    const currentUser = useSelector(selectUser);
    const isLoggedIn = useSelector(selectIsLoggedIn);
    const dispatch = useDispatch();

    let location = useLocation();

    useEffect(() => {
        if (["/login", "/register"].includes(location.pathname)) {
            dispatch(clearMessage()); // clear message when changing location
        }
    }, [dispatch, location]);

    const logOut = useCallback(() => {
        dispatch(logout());
    }, [dispatch]);

    useEffect(() => {
        if (currentUser) {
            setShowModeratorBoard(currentUser.roles.includes("ROLE_MODERATOR"));
            setShowAdminBoard(currentUser.roles.includes("ROLE_ADMIN"));
        } else {
            setShowModeratorBoard(false);
            setShowAdminBoard(false);
        }
    }, [currentUser]);

    return (
        <div className="">
            <nav className="navbar navbar-expand navbar-dark bg-dark mynav">
                <div>
                    <Link
                        to={"/"}
                        className="btn btn-secondary mynav-btn"
                        onClick={!asideCollapsed && toggleAside}
                    >
                        <i className="bi bi-house-heart"></i>
                    </Link>{" "}
                    {currentUser && (
                        <button
                            className="btn btn-outline-secondary mynav-btn"
                            onClick={toggleAside}
                        >
                            <i className="bi bi-list"></i>
                        </button>
                    )}
                </div>

                {currentUser ? (
                    <div className="navbar-nav ml-auto">
                        <li className="nav-item">
                            <Link
                                to={"/profile"}
                                className="nav-link"
                                onClick={!asideCollapsed && toggleAside}
                            >
                                {currentUser.username}
                            </Link>
                        </li>
                        <li className="nav-item">
                            <a
                                href="/login"
                                className="nav-link"
                                onClick={logOut}
                            >
                                Logout
                            </a>
                        </li>
                    </div>
                ) : (
                    <div className="navbar-nav ml-auto">
                        <li className="nav-item">
                            <Link to={"/login"} className="nav-link">
                                Login
                            </Link>
                        </li>

                        <li className="nav-item">
                            <Link to={"/register"} className="nav-link">
                                Sign Up
                            </Link>
                        </li>
                    </div>
                )}
            </nav>

            {currentUser ? (
                <div className="bd-layout">
                    {/* MODULES VERTICAL NAVBAR */}
                    <aside
                        className={`bd-sidebar ${
                            asideCollapsed ? "collapsed" : ""
                        }`}
                    >
                        <ul className="navbar-nav">
                            {/* {showAdminBoard && (
                                <li className="nav-item">
                                    <Link to={"/admin"} className="nav-link">
                                        AdminBoard
                                    </Link>
                                </li>
                            )} */}

                            {/* {showModeratorBoard && (
                                <li className="nav-item">
                                    <Link to={"/mod"} className="nav-link">
                                        ModeratorBoard
                                    </Link>
                                </li>
                            )} */}

                            {/* {currentUser && (
                                <li className="nav-item">
                                    <Link to={"/user"} className="nav-link">
                                        UserBoard
                                    </Link>
                                </li>
                            )} */}

                            {currentUser.roles.includes("ROLE_ADMIN") && (
                                <li className="nav-item" onClick={toggleAside}>
                                    <Link to={"/members"} className="nav-link">
                                        Family Members
                                    </Link>
                                </li>
                            )}

                            {currentUser && (
                                <li className="nav-item" onClick={toggleAside}>
                                    <Link
                                        to={"/costCenters"}
                                        className="nav-link"
                                    >
                                        Cost Centers
                                    </Link>
                                </li>
                            )}

                            {currentUser && (
                                <li className="nav-item" onClick={toggleAside}>
                                    <Link
                                        to={"/suppliers"}
                                        className="nav-link"
                                    >
                                        Suppliers
                                    </Link>
                                </li>
                            )}

                            {currentUser && (
                                <li className="nav-item" onClick={toggleAside}>
                                    <Link to={"/payments"} className="nav-link">
                                        Payments
                                    </Link>
                                </li>
                            )}
                        </ul>
                    </aside>

                    {/* MODULES VIEW */}
                    <main className="bd-main">
                        <Routes>
                            <Route path="/" element={<Home />} />
                            <Route path="/login" element={<Login />} />
                            <Route
                                path="/profile"
                                element={
                                    <ProtectedRoute
                                        key={"/profile"}
                                        component={Profile}
                                    />
                                }
                            />
                            {/* <Route path="/user" element={<BoardUser />} /> */}
                            {/* <Route path="/mod" element={<BoardModerator />} /> */}
                            {/* <Route path="/admin" element={<BoardAdmin />} /> */}

                            {/* MEMBERS MODULE */}
                            <Route
                                path="/members"
                                // element={<MemberList />}
                                element={
                                    <ProtectedRoute
                                        key={"/members"}
                                        component={MemberList}
                                    />
                                }
                            />
                            <Route
                                path="/members/add"
                                // element={<AddMember />}
                                element={
                                    <ProtectedRoute
                                        key={"/members/add"}
                                        component={AddMember}
                                    />
                                }
                            />
                            <Route
                                path="/members/:id"
                                // element={<Member />}
                                element={
                                    <ProtectedRoute
                                        key={"/members/:id"}
                                        component={Member}
                                    />
                                }
                            />
                            {/* COST CENTERS MODULE */}
                            <Route
                                path="/costCenters"
                                // element={<CostCenterList />}
                                element={
                                    <ProtectedRoute
                                        key={"/costCenters"}
                                        component={CostCenterList}
                                    />
                                }
                            />
                            <Route
                                path="/costCenters/add"
                                // element={<AddCostCenter />}
                                element={
                                    <ProtectedRoute
                                        key={"/costCenters/add"}
                                        component={AddCostCenter}
                                    />
                                }
                            />
                            <Route
                                path="/costCenters/:id"
                                // element={<CostCenter />}
                                element={
                                    <ProtectedRoute
                                        key={"/costCenters/:id"}
                                        component={CostCenter}
                                    />
                                }
                            />
                            {/* SUPPLIERS MODULE */}
                            <Route
                                path="/suppliers"
                                // element={<SupplierList />}
                                element={
                                    <ProtectedRoute
                                        key={"/suppliers"}
                                        component={SupplierList}
                                    />
                                }
                            />
                            <Route
                                path="/suppliers/add"
                                // element={<AddSupplier />}
                                element={
                                    <ProtectedRoute
                                        key={"/suppliers/add"}
                                        component={AddSupplier}
                                    />
                                }
                            />
                            <Route
                                path="/suppliers/:id"
                                // element={<Supplier />}
                                element={
                                    <ProtectedRoute
                                        key={"/suppliers/:id"}
                                        component={Supplier}
                                    />
                                }
                            />
                            {/* PAYMENTS MODULE */}
                            <Route
                                path="/payments"
                                // element={<PaymentList />}
                                element={
                                    <ProtectedRoute
                                        key={"/payments"}
                                        component={PaymentList}
                                    />
                                }
                            />
                            <Route
                                path="/payments/add"
                                // element={<AddPayment />}
                                element={
                                    <ProtectedRoute
                                        key={"/payments/add"}
                                        component={AddPayment}
                                    />
                                }
                            />
                            <Route
                                path="/payments/:id"
                                // element={<Payment />}
                                element={
                                    <ProtectedRoute
                                        key={"/payments/:id"}
                                        component={Payment}
                                    />
                                }
                            />
                        </Routes>
                    </main>
                </div>
            ) : (
                <Routes>
                    <Route path="/" element={<Home />} />
                    <Route
                        path="/*"
                        element={<ProtectedRoute key={"/*"} component={Home} />}
                    />
                    <Route
                        path="/login"
                        element={
                            !isLoggedIn ? (
                                <Login />
                            ) : (
                                <Navigate replace to={"/profile"} />
                            )
                        }
                    />
                    <Route path="/register" element={<Register />} />
                </Routes>
            )}
        </div>
    );
};

export default App;
